import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Shield } from "lucide-react";
import { useForm, ValidationError } from '@formspree/react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';

const DemoRequestModal = ({ isOpen, onClose }) => {
  const [state, handleSubmit] = useForm("mzzjawdw");
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Security: Input sanitization patterns
  const patterns = {
    name: /^[a-zA-Z\s'-]{2,50}$/,
    email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    company: /^[a-zA-Z0-9\s&.,'()-]{2,100}$/,
    phone: /^[\d\s()+-]{10,20}$/
  };

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  // Security: Rate limiting state
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [lastSubmitTime, setLastSubmitTime] = useState(0);

  // Close modal and reset form on success
  useEffect(() => {
    if (state.succeeded && isOpen) {
      const timer = setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
        setErrors({});
        setTouched({});
      }, 6000); // 6 seconds to read the success message
      return () => clearTimeout(timer);
    }
  }, [state.succeeded, isOpen, onClose]);

  // Monitor Formspree state changes
  useEffect(() => {
    console.log('Formspree state changed:', {
      submitting: state.submitting,
      succeeded: state.succeeded,
      errors: state.errors,
      result: state.result
    });
    
    if (state.succeeded) {
      console.log('🎉 SUCCESS! Form submitted successfully to Formspree!');
    }
    
    if (state.errors) {
      console.error('❌ Formspree errors:', state.errors);
    }
  }, [state.submitting, state.succeeded, state.errors, state.result]);

  // Security: Prevent modal content from being selected/copied easily
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Custom validation with detailed feedback
  const validateField = (name, value) => {
    if (!value.trim() && name !== 'phone' && name !== 'message') {
      return 'This field is required';
    }
    
    // Specific validation rules
    switch(name) {
      case 'name':
        if (value.length < 2) return 'Name must be at least 2 characters';
        if (value.length > 50) return 'Name must be less than 50 characters';
        if (!patterns.name.test(value)) return 'Name can only contain letters, spaces, hyphens, and apostrophes';
        break;
      case 'email':
        if (!patterns.email.test(value)) return 'Please enter a valid email address';
        // Check for common disposable email domains
        const disposableDomains = ['tempmail.com', 'throwaway.email', '10minutemail.com'];
        const domain = value.split('@')[1]?.toLowerCase();
        if (disposableDomains.includes(domain)) return 'Please use a business email address';
        break;
      case 'company':
        if (value.length < 2) return 'Company name must be at least 2 characters';
        if (value.length > 100) return 'Company name must be less than 100 characters';
        if (!patterns.company.test(value)) return 'Please enter a valid company name';
        break;
      case 'phone':
        if (value && !patterns.phone.test(value)) return 'Please enter a valid phone number';
        break;
      case 'message':
        if (value.length > 500) return 'Message must be less than 500 characters';
        break;
      default:
        break;
    }
    
    return '';
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    
    // Security: Sanitize input (remove potential XSS)
    const sanitizedValue = value.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    
    // Only validate if field has been touched
    if (touched[name]) {
      const error = validateField(name, sanitizedValue);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Security: Rate limiting check
  const checkRateLimit = () => {
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTime;
    
    // Allow max 3 submissions per 5 minutes
    if (timeSinceLastSubmit < 300000 && submitAttempts >= 3) {
      return false;
    }
    
    // Reset counter after 5 minutes
    if (timeSinceLastSubmit >= 300000) {
      setSubmitAttempts(0);
    }
    
    return true;
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submitted with data:', formData);
    console.log('Formspree state:', state);
    
    // Security: Check rate limit
    if (!checkRateLimit()) {
      setErrors(prev => ({ 
        ...prev, 
        submit: 'Too many attempts. Please try again in a few minutes.' 
      }));
      return;
    }
    
    // Validate all fields before submit
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      console.log('Validation errors:', newErrors);
      return;
    }
    
    // Execute reCAPTCHA (if available)
    if (executeRecaptcha) {
      try {
        console.log('Executing reCAPTCHA...');
        const token = await executeRecaptcha('submit_demo_form');
        console.log('reCAPTCHA token obtained:', token.substring(0, 20) + '...');
        
        // Add reCAPTCHA token to form data
        const formElement = e.target;
        let tokenInput = formElement.querySelector('input[name="g-recaptcha-response"]');
        if (!tokenInput) {
          tokenInput = document.createElement('input');
          tokenInput.type = 'hidden';
          tokenInput.name = 'g-recaptcha-response';
          formElement.appendChild(tokenInput);
        }
        tokenInput.value = token;
      } catch (error) {
        console.error('reCAPTCHA error:', error);
        console.log('Continuing without reCAPTCHA...');
      }
    } else {
      console.log('⚠️ reCAPTCHA not configured - form will submit without it');
    }
    
    // Update rate limiting
    setSubmitAttempts(prev => prev + 1);
    setLastSubmitTime(Date.now());
    
    // Submit form - Formspree will handle the actual submission
    console.log('Submitting to Formspree...');
    await handleSubmit(e);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-8 transform transition-all">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>

          {/* Success Message */}
          {state.succeeded ? (
            <div className="text-center py-8 px-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Request Submitted Successfully!
              </h3>
              <p className="text-lg text-gray-700 mb-6">
                Thank you for your interest in Zeelium!
              </p>

              <p className="text-xs text-gray-400 mt-4">
                This window will close automatically in a few seconds...
              </p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Request a Demo
                </h2>
                <p className="text-gray-600">
                  See how Zeelium can transform your security operations
                </p>
              </div>

              {/* Form Error Message */}
              {(state.errors && state.errors.length > 0) || errors.submit ? (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    {errors.submit || 'There was an error submitting the form. Please try again.'}
                  </div>
                </div>
              ) : null}

              {/* Form */}
              <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
                {/* Honeypot for spam protection */}
                <input 
                  type="text" 
                  name="_gotcha" 
                  style={{ display: 'none' }} 
                  tabIndex="-1" 
                  autoComplete="off"
                />

                {/* Full Name */}
                <div>
                  <label 
                    htmlFor="name" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    required
                    maxLength="50"
                    autoComplete="name"
                    className={`w-full px-4 py-2.5 border ${
                      touched.name && errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="John Doe"
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                  />
                  {touched.name && errors.name && (
                    <p id="name-error" className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.name}
                    </p>
                  )}
                  <ValidationError prefix="Name" field="name" errors={state.errors} />
                </div>

                {/* Work Email */}
                <div>
                  <label 
                    htmlFor="email" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Work Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    required
                    autoComplete="email"
                    className={`w-full px-4 py-2.5 border ${
                      touched.email && errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="john.doe@company.com"
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {touched.email && errors.email && (
                    <p id="email-error" className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.email}
                    </p>
                  )}
                  <ValidationError prefix="Email" field="email" errors={state.errors} />
                </div>

                {/* Company Name */}
                <div>
                  <label 
                    htmlFor="company" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    required
                    maxLength="100"
                    autoComplete="organization"
                    className={`w-full px-4 py-2.5 border ${
                      touched.company && errors.company ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="Acme Corp"
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    aria-required="true"
                    aria-invalid={!!errors.company}
                    aria-describedby={errors.company ? "company-error" : undefined}
                  />
                  {touched.company && errors.company && (
                    <p id="company-error" className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.company}
                    </p>
                  )}
                  <ValidationError prefix="Company" field="company" errors={state.errors} />
                </div>

                {/* Phone Number (Optional) */}
                <div>
                  <label 
                    htmlFor="phone" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Phone Number <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    maxLength="20"
                    autoComplete="tel"
                    className={`w-full px-4 py-2.5 border ${
                      touched.phone && errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="+1 (555) 123-4567"
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "phone-error" : undefined}
                  />
                  {touched.phone && errors.phone && (
                    <p id="phone-error" className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.phone}
                    </p>
                  )}
                  <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                </div>

                {/* Message (Optional) */}
                <div>
                  <label 
                    htmlFor="message" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Message <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    rows="4"
                    maxLength="500"
                    className={`w-full px-4 py-2.5 border ${
                      touched.message && errors.message ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none`}
                    placeholder="Tell us about your security challenges..."
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "message-error" : undefined}
                  ></textarea>
                  <div className="flex justify-between items-center mt-1">
                    {touched.message && errors.message ? (
                      <p id="message-error" className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {errors.message}
                      </p>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {formData.message.length}/500 characters
                      </span>
                    )}
                  </div>
                  <ValidationError prefix="Message" field="message" errors={state.errors} />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={
                    state.submitting || 
                    Object.keys(errors).some(key => key !== 'submit' && errors[key]) ||
                    !formData.name || 
                    !formData.email || 
                    !formData.company
                  }
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-md hover:shadow-lg"
                >
                  {state.submitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Securely...
                    </span>
                  ) : (
                    'Request Demo'
                  )}
                </button>

                {/* Privacy Notice */}
                <p className="text-xs text-gray-500 text-center mt-4">
                  By submitting this form, you agree to our{' '}
                  <Link to="/privacy-policy" onClick={onClose} className="text-blue-600 hover:underline">
                    Privacy Policy
                  </Link>
                  . We respect your privacy and will never share your information.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoRequestModal;

